import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, MessageSquare, Clock, Shield, Car } from 'lucide-react';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/01a3021f0b936905117a253e9e27089f638f7de74d1b36f49d74b5854dc5e397.png";

const included = [
  'Professional chauffeur',
  'Premium SUV',
  'Bottled water',
  'Wi-Fi & charging',
  'Real-time updates',
  'Luggage assistance',
];

const quoteReasons = [
  {
    icon: Car,
    title: 'Vehicle Selection',
    desc: 'Choose from our curated fleet — Cadillac Escalade, Mercedes S-Class, or Chevrolet Suburban.',
  },
  {
    icon: Clock,
    title: 'Duration & Distance',
    desc: 'Whether a quick airport transfer or a full-day itinerary, your quote reflects your exact needs.',
  },
  {
    icon: MessageSquare,
    title: 'Personal Consultation',
    desc: 'We discuss your requirements directly — no algorithms, no surge pricing, just honest conversation.',
  },
  {
    icon: Shield,
    title: 'No Hidden Fees',
    desc: 'Your quote is your price. Transparent, upfront, and all-inclusive with our premium amenities.',
  },
];

export const ChauffeurRates = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="chauffeur-rates-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="rates-hero" className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '90vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.42) saturate(0.9) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 35%, rgba(194,168,120,0.06) 0%, transparent 55%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.3) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '180px', paddingBottom: '160px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '32px' }}>
              Pricing
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="rates-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(38px, 5.5vw, 64px)', fontWeight: 500, color: '#FAFAFA', marginBottom: '28px', lineHeight: 1.08,
              textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              Every Journey Is Unique. So Is Your Quote.
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '17px', color: 'rgba(220,220,220,0.6)', fontWeight: 300, lineHeight: 1.8, maxWidth: '520px', margin: '0 auto 52px' }}>
              We believe in personalized service — not one-size-fits-all pricing. Tell us about your journey and receive a tailored quote within the hour.
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.8s' }}>
            <button
              data-testid="hero-request-quote-btn"
              onClick={() => navigate('/chauffeur/book')}
              style={{
                backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 44px',
                fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
            >
              Request a Quote
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '160px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── HOW PRICING WORKS — Glass Cards ── */}
      <section data-testid="rates-how-it-works" className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A', padding: '8rem 1.5rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 30%, rgba(194,168,120,0.03) 0%, transparent 50%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.03 }} />

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center fade-element" style={{ marginBottom: '72px' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '24px', opacity: 0.6 }} />
            <h2 className="font-serif" style={{
              fontSize: 'clamp(28px, 3.5vw, 42px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '20px',
              textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}>
              How Our Pricing Works
            </h2>
            <p style={{ fontSize: '15px', color: 'rgba(225,225,225,0.8)', maxWidth: '480px', margin: '0 auto', fontWeight: 300, lineHeight: 1.8 }}>
              Your quote is based on the specifics of your journey — tailored, transparent, and fair.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '24px' }}>
            {quoteReasons.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="fade-element"
                  style={{
                    padding: '44px 36px',
                    backgroundColor: 'rgba(14,14,14,0.45)',
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(194,168,120,0.06)',
                    transition: 'border-color 600ms ease, box-shadow 600ms ease, transform 400ms ease',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(194,168,120,0.16)';
                    e.currentTarget.style.boxShadow = '0 12px 48px rgba(0,0,0,0.3)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(194,168,120,0.06)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.1), transparent)',
                  }} />
                  <div style={{
                    width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(194,168,120,0.04)', border: '1px solid rgba(194,168,120,0.08)',
                    marginBottom: '24px',
                  }}>
                    <Icon size={18} strokeWidth={1.5} style={{ color: '#C2A878', opacity: 0.6 }} />
                  </div>
                  <h3 className="font-serif" style={{ fontSize: '19px', fontWeight: 400, color: '#F5F5F5', marginBottom: '12px' }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', lineHeight: 1.9, fontWeight: 300 }}>
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ── */}
      <section data-testid="rates-included" className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.12) saturate(0.45)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.7) 30%, rgba(10,10,10,0.7) 70%, rgba(10,10,10,0.97) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 50%, rgba(194,168,120,0.04) 0%, transparent 45%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.03 }} />

        <div className="max-w-4xl mx-auto relative z-10" style={{ padding: '8rem 1.5rem' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center" style={{ gap: '72px' }}>
            <div className="fade-element">
              <div className="gold-divider" style={{ marginBottom: '24px', opacity: 0.5 }} />
              <h2 className="font-serif" style={{
                fontSize: 'clamp(28px, 3.5vw, 42px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '20px',
              }}>
                Every Quote Includes
              </h2>
              <p style={{ fontSize: '15px', color: 'rgba(225,225,225,0.8)', fontWeight: 300, lineHeight: 1.8 }}>
                No matter the journey, every booking comes with our full premium experience as standard — no add-ons, no surprises.
              </p>
            </div>
            <div className="fade-element">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {included.map((item, i) => (
                  <div key={i} className="flex items-center" style={{ gap: '14px' }}>
                    <div style={{
                      width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'rgba(194,168,120,0.06)', flexShrink: 0,
                    }}>
                      <Check size={13} style={{ color: '#C2A878', opacity: 0.7 }} />
                    </div>
                    <span style={{ fontSize: '15px', color: 'rgba(225,225,225,0.85)', fontWeight: 300 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA — Request a Quote ── */}
      <section data-testid="rates-cta" className="relative overflow-hidden" style={{ minHeight: '550px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.15) saturate(0.5)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.55) 35%, rgba(10,10,10,0.55) 65%, rgba(10,10,10,0.97) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.06) 0%, transparent 45%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="max-w-3xl mx-auto text-center relative z-10" style={{ padding: '10rem 1.5rem' }}>
          <div className="fade-element">
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.6 }} />
            <h2 className="font-serif" style={{
              fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '20px', lineHeight: 1.12, textShadow: '0 2px 30px rgba(0,0,0,0.4)',
            }}>
              Ready to Plan Your Journey?
            </h2>
            <p style={{
              fontSize: '16px', color: 'rgba(220,220,220,0.5)', fontWeight: 300,
              lineHeight: 1.8, maxWidth: '460px', margin: '0 auto 48px',
            }}>
              Share your travel details and we'll provide a personalized quote — typically within the hour.
            </p>
            <div className="flex flex-wrap justify-center" style={{ gap: '16px' }}>
              <button
                data-testid="rates-request-quote-btn"
                onClick={() => navigate('/chauffeur/book')}
                style={{
                  backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 44px',
                  fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                  border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                  boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
              >
                Request a Quote
              </button>
              <a
                href="tel:+14379963801"
                data-testid="rates-call-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  backgroundColor: 'transparent', color: '#C2A878', padding: '15px 40px',
                  fontSize: '12px', fontWeight: 400, letterSpacing: '1.2px', textTransform: 'uppercase',
                  border: '1px solid rgba(194,168,120,0.3)', textDecoration: 'none',
                  cursor: 'pointer', transition: 'all 400ms ease',
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(194,168,120,0.08)'; e.target.style.borderColor = 'rgba(194,168,120,0.5)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(194,168,120,0.3)'; }}
              >
                Call Us Directly
              </a>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};
